import { Upload } from 'lucide-react';
import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useUploadsEnabled } from '../../lib/use-uploads-enabled';
import { Button, type ButtonProps } from './button';

/**
 * Nút tải lên tự khoá kèm **lý do** khi chưa cấu hình nơi lưu trữ —
 * docs/01-tech-stack.md §5.5.
 *
 * Tồn tại thành một component dùng chung thay vì để mỗi màn hình tự kiểm: mỗi
 * chỗ tự kiểm là mỗi chỗ có thể quên, và chỗ quên sẽ hiện một nút bấm được rồi
 * báo lỗi 503 — đúng trải nghiệm mà §5.5 muốn tránh.
 */
export function UploadButton({
  children,
  ...props
}: ButtonProps & { children?: ReactNode }): ReactNode {
  const { t } = useTranslation();
  const { enabled, reason } = useUploadsEnabled();

  const blocked = enabled !== true;

  return (
    <Button
      {...props}
      disabled={props.disabled || blocked}
      // `title` để lý do hiện ra khi rê chuột — nút bị khoá mà không nói vì sao
      // là thứ khiến người dùng đi mở ticket.
      title={blocked ? (reason ?? t('common.loading')) : undefined}
      aria-describedby={blocked ? 'uploads-disabled-reason' : undefined}
    >
      <Upload aria-hidden />
      {children ?? t('files.upload')}
      {blocked && reason && (
        <span id="uploads-disabled-reason" className="sr-only">
          {reason}
        </span>
      )}
    </Button>
  );
}
